import React, { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react'
import { useDebounce } from 'use-debounce'
import { makeRequest } from '../apis/makeRequest'
import { getUsers, deleteUser } from '../apis/accountRequests'
import { useNavigate } from "react-router-dom"
import UserDetails from './UserDetails'
import { useDispatch, useSelector } from 'react-redux'
import { notificationActions } from '../actions/notificationActions'
import { Table, TableContainer, TableCell, TableHead, TableRow, Paper, TableBody, IconButton, Typography, Button, Divider, Dialog, Toolbar, DialogContent, DialogActions, DialogTitle, TextField } from '@mui/material'
import { Box, styled } from '@mui/system'
import CreateIcon from '@mui/icons-material/Create'
import DeleteIcon from '@mui/icons-material/Delete'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'

const HeadTableCell = styled(TableCell)(({ theme }) => ({
    backgroundColor: "#e0e0e0",
    color: theme.palette.common.black,
    fontSize: 16,
}))

const BodyTableCell = styled(TableCell)({
    backgroundColor: "#f5f5f5",
    fontSize: 15,
})

const PAGE_SIZE = 50

export default function Users() {

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { dynamicsId, roles, salesData } = useSelector(state => state.account)

    const [users, setUsers] = useState([])
    const [open, setOpen] = useState(false)
    const [deleteUsers, setDeleteUsers] = useState(false)
    const [borrar, setBorrar] = useState({})
    const [editUser, setEditUser] = useState({})
    const [searchTerm, setSearchTerm] = useState('')
    const [debouncedSearch] = useDebounce(searchTerm, 250)

    const filteredUsers = useMemo(() => {
        const q = debouncedSearch.trim().toLowerCase()
        if (!q) {
            return users
        }
        return users.filter((user) => {
            const userName = user.userName?.toLowerCase() ?? ''
            const email = user.email?.toLowerCase() ?? ''
            const role = user.roles?.[0]?.toLowerCase() ?? ''
            return userName.includes(q) || email.includes(q) || role.includes(q)
        })
    }, [users, debouncedSearch])

    const isFilterPending = searchTerm.trim() !== debouncedSearch.trim()

    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
    const scrollContainerRef = useRef(null)
    const loadMoreSentinelRef = useRef(null)

    const resetPagination = useCallback((total) => {
        setVisibleCount(Math.min(PAGE_SIZE, total))
    }, [])

    const totalFiltered = filteredUsers.length

    const visibleUsers = useMemo(
        () => filteredUsers.slice(0, visibleCount),
        [filteredUsers, visibleCount]
    )

    const lastChunkLoadAtRef = useRef(0)

    const loadNextChunk = useCallback(() => {
        setVisibleCount((c) => {
            if (c >= totalFiltered) return c
            return Math.min(c + PAGE_SIZE, totalFiltered)
        })
    }, [totalFiltered])

    useLayoutEffect(() => {
        resetPagination(totalFiltered)
    }, [debouncedSearch, totalFiltered, resetPagination])

    useLayoutEffect(() => {
        const el = scrollContainerRef.current
        if (!el || visibleCount >= totalFiltered) return
        if (el.scrollHeight <= el.clientHeight + 2) {
            loadNextChunk()
        }
    }, [totalFiltered, visibleCount, loadNextChunk, visibleUsers.length])

    useEffect(() => {
        const root = scrollContainerRef.current
        const target = loadMoreSentinelRef.current
        if (!root || !target || totalFiltered === 0) return
        if (visibleCount >= totalFiltered) return

        const observer = new IntersectionObserver(
            (entries) => {
                const e = entries[0]
                if (!e.isIntersecting) return
                const now = performance.now()
                if (now - lastChunkLoadAtRef.current < 200) return
                lastChunkLoadAtRef.current = now
                loadNextChunk()
            },
            { root, rootMargin: '0px 0px 200px 0px', threshold: 0 }
        )
        observer.observe(target)
        return () => observer.disconnect()
    }, [loadNextChunk, totalFiltered, visibleCount, visibleUsers.length])

    useEffect(() => {
        if (users.length !== 0)
            return

        makeRequest(
            () => getUsers(),
            (res) => {
                if (!roles.includes("Administrador") && !roles.includes("Admin Ventas")) {
                    const findUser = res.data.filter(user => user.dynamicsId === dynamicsId || user.dynamicsId === salesData.dynamicsId)
                    setUsers(findUser)
                    return
                }
                setUsers(res.data)
            }
        )
    }, [users, roles, dynamicsId, salesData.dynamicsId])

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value)
    }

    const handleClick = (event, item) => {
        setOpen(true)
        setEditUser(item)
    }

    const handleDelete = (event, item) => {
        setDeleteUsers(true)
        setBorrar(item)
    }

    const deleteData = () => {
        makeRequest(
            () => deleteUser(borrar.userName),
            (res) => {
                setDeleteUsers(false)
                dispatch(notificationActions.showSuccess({ message: `Se elimino el usuario : ${borrar.userName}` }))
                navigate('/menu')
                setUsers([])
            },
            (err) => dispatch(notificationActions.showError({ message: `Ha ocurrido un error al eliminar el usuario` }))
        )
    }


    return <>
         <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                <SearchIcon sx={{ color: 'text.secondary' }} />
                <TextField
                    size="small"
                    placeholder="Buscar por usuario, email o rol..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    variant="outlined"
                    sx={{
                        width: 220,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#f5f5f5',
                            '&:hover': {
                                backgroundColor: '#eeeeee',
                            },
                            '&.Mui-focused': {
                                backgroundColor: '#ffffff',
                            }
                        }
                    }}
                />
                {searchTerm && (
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {isFilterPending
                            ? 'Filtrando…'
                            : `${filteredUsers.length} resultado${filteredUsers.length !== 1 ? 's' : ''}`}
                    </Typography>
                )}
            </Box>

            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                <Typography variant='h5'>Usuarios</Typography>
            </Box>

            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                {(roles.includes("Administrador") || roles.includes("Admin Ventas") || roles.includes("Usuario")) &&
                    <Button size='small' style={{ background: "green", height: 30, color: "white" }}
                        onClick={() => { setOpen(true); setEditUser({}) }}>
                        <AddIcon />
                    </Button>}
            </Box>
        </Box>
        <Divider style={{ marginBottom: '1em' }} />
        <TableContainer
            ref={scrollContainerRef}
            component={Paper}
            sx={{ maxHeight: { xs: 360, sm: 480, md: '65vh' }, overflow: 'auto' }}
        >
            <Table size='small' stickyHeader>
                <TableHead>
                    <TableRow>
                        <HeadTableCell align='center'>Usuario</HeadTableCell>
                        <HeadTableCell align='center'>Roles</HeadTableCell>
                        <HeadTableCell align='center'>Email</HeadTableCell>
                        <HeadTableCell align='center'>dynamicsId</HeadTableCell>
                        {(roles.includes("Administrador") || roles.includes("Admin Ventas")) && <HeadTableCell align='center'>Editar</HeadTableCell>}
                        {(roles.includes("Administrador") || roles.includes("Admin Ventas")) && <HeadTableCell align='center'>Borrar</HeadTableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {visibleUsers.map((item) => (
                        <TableRow key={item.id}>
                            <BodyTableCell align='center'>{item.userName}</BodyTableCell>
                            <BodyTableCell align='center'>{item.roles?.[0]}</BodyTableCell>
                            <BodyTableCell align='center'>{item.email}</BodyTableCell>
                            <BodyTableCell align='center'>{item.dynamicsId}</BodyTableCell>
                            {(roles.includes("Administrador") || roles.includes("Admin Ventas")) && <BodyTableCell align='center'>
                                    <IconButton edge='end' onClick={(event) => handleClick(event, item)} size="large"><CreateIcon /></IconButton>
                            </BodyTableCell>}
                            {(roles.includes("Administrador") || roles.includes("Admin Ventas")) && <BodyTableCell align='center'>
                                <IconButton
                                    color='inherit'
                                    onClick={(event) => handleDelete(event, item)}
                                    size="large"><DeleteIcon /></IconButton>
                            </BodyTableCell>}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {totalFiltered > 0 && visibleCount < totalFiltered && (
                <Box
                    ref={loadMoreSentinelRef}
                    aria-hidden
                    sx={{ height: 12, width: '100%' }}
                />
            )}
        </TableContainer>
        {totalFiltered > 0 && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.75, ml: 0.5, mb: 0 }}>
                Mostrando {visibleUsers.length} de {totalFiltered}
                {visibleCount < totalFiltered && ' — deslizá o seguí bajando para cargar más'}
            </Typography>
        )}
        <Dialog maxWidth='lg' open={open} onClose={() => setOpen(false)}>
            <Toolbar variant='dense'>
                <IconButton
                    style={{ position: 'absolute', right: 10, top: 1, color: 'inherit' }}
                    onClick={() => setOpen(false)}
                    size="large">
                    <CloseIcon />
                </IconButton>
            </Toolbar>
            <Divider />
            <DialogContent>
                <UserDetails row={editUser} setter={setOpen} setters={setUsers} />
            </DialogContent>
        </Dialog>
        <Dialog open={deleteUsers} onClose={() => setDeleteUsers(false)}>
            <DialogTitle style={{ backgroundColor: 'white', color: 'black' }}>Desea eliminar el usuario: {borrar?.userName}?</DialogTitle>
            <DialogActions>
                <Button onClick={() => setDeleteUsers(false)} size='small'> Cancelar</Button>
                <Button onClick={() => deleteData()} size='small'> Borrar</Button>
            </DialogActions>
        </Dialog>
    </>;
}